// App events: the card Chat shows once an App's webhook is on (set_app_event).
//
// Namespace `app-events`; owner: the App functions workstream (ui/workspace/app-event-card.tsx).
// The signing secret is revealed only in this card, to the App's owner; no copy ever repeats it.
// Placeholders are `{name}`, filled with `formatCopy`.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Webhook for {app}',
  urlLabel: 'Webhook URL',
  copy: 'Copy',
  copied: 'Copied',
  secretLabel: 'Signing secret',
  showSecret: 'Show signing secret',
  hideSecret: 'Hide',
  revealing: 'Showing…',
  how: 'The sender posts JSON to this URL with x-poneva-timestamp (unix seconds) and x-poneva-signature, the hex HMAC-SHA256 of “<timestamp>.<body>” under the signing secret.',
  ownerOnly: 'Only the App’s owner can see the signing secret.',
  notEnabled: 'This webhook is off, so it has no signing secret.',
  failed: 'The signing secret couldn’t be shown. Try again.',
  privacy: 'The signing secret isn’t part of the chat. The model never sees it.',
};

export type AppEventsCopyKey = keyof typeof en;

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    title: 'Webhook за {app}',
    urlLabel: 'URL на webhook',
    copy: 'Копиране',
    copied: 'Копирано',
    secretLabel: 'Таен ключ за подписване',
    showSecret: 'Показване на тайния ключ за подписване',
    hideSecret: 'Скриване',
    revealing: 'Показване…',
    how: 'Подателят изпраща JSON чрез POST към този URL с x-poneva-timestamp (unix секунди) и x-poneva-signature – hex HMAC-SHA256 на „<timestamp>.<body>“ с тайния ключ за подписване.',
    ownerOnly: 'Само собственикът на приложението може да види тайния ключ за подписване.',
    notEnabled: 'Този webhook е изключен, затова няма таен ключ за подписване.',
    failed: 'Тайният ключ за подписване не можа да се покаже. Опитайте отново.',
    privacy: 'Тайният ключ за подписване не е част от чата. Моделът никога не го вижда.',
  },
  hr: {
    title: 'Webhook za aplikaciju {app}',
    urlLabel: 'URL webhooka',
    copy: 'Kopiraj',
    copied: 'Kopirano',
    secretLabel: 'Potpisni ključ',
    showSecret: 'Prikaži potpisni ključ',
    hideSecret: 'Sakrij',
    revealing: 'Prikazivanje…',
    how: 'Pošiljatelj šalje JSON na ovaj URL sa zaglavljima x-poneva-timestamp (unix sekunde) i x-poneva-signature, tj. heksadecimalnim HMAC-SHA256 niza „<timestamp>.<body>“ potpisanim potpisnim ključem.',
    ownerOnly: 'Potpisni ključ može vidjeti samo vlasnik aplikacije.',
    notEnabled: 'Ovaj je webhook isključen, pa nema potpisni ključ.',
    failed: 'Potpisni ključ nije bilo moguće prikazati. Pokušajte ponovno.',
    privacy: 'Potpisni ključ nije dio chata. Model ga nikad ne vidi.',
  },
  cs: {
    title: 'Webhook aplikace {app}',
    urlLabel: 'URL webhooku',
    copy: 'Kopírovat',
    copied: 'Zkopírováno',
    secretLabel: 'Podpisový klíč',
    showSecret: 'Zobrazit podpisový klíč',
    hideSecret: 'Skrýt',
    revealing: 'Zobrazuji…',
    how: 'Odesílatel posílá JSON na tuto URL s hlavičkami x-poneva-timestamp (unixové sekundy) a x-poneva-signature, tedy hexadecimálním HMAC-SHA256 řetězce „<timestamp>.<body>“ podepsaným podpisovým klíčem.',
    ownerOnly: 'Podpisový klíč vidí jen vlastník aplikace.',
    notEnabled: 'Tento webhook je vypnutý, proto nemá podpisový klíč.',
    failed: 'Podpisový klíč se nepodařilo zobrazit. Zkuste to znovu.',
    privacy: 'Podpisový klíč není součástí chatu. Model ho nikdy nevidí.',
  },
  da: {
    title: 'Webhook til {app}',
    urlLabel: 'Webhook-URL',
    copy: 'Kopiér',
    copied: 'Kopieret',
    secretLabel: 'Signeringshemmelighed',
    showSecret: 'Vis signeringshemmeligheden',
    hideSecret: 'Skjul',
    revealing: 'Viser…',
    how: 'Afsenderen sender JSON til denne URL med x-poneva-timestamp (unix-sekunder) og x-poneva-signature, den hex-kodede HMAC-SHA256 af “<timestamp>.<body>” med signeringshemmeligheden.',
    ownerOnly: 'Kun appens ejer kan se signeringshemmeligheden.',
    notEnabled: 'Denne webhook er slået fra, så den har ingen signeringshemmelighed.',
    failed: 'Signeringshemmeligheden kunne ikke vises. Prøv igen.',
    privacy: 'Signeringshemmeligheden er ikke en del af chatten. Modellen ser den aldrig.',
  },
  nl: {
    title: 'Webhook voor {app}',
    urlLabel: 'Webhook-URL',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    secretLabel: 'Ondertekeningsgeheim',
    showSecret: 'Ondertekeningsgeheim tonen',
    hideSecret: 'Verbergen',
    revealing: 'Tonen…',
    how: 'De afzender post JSON naar deze URL met x-poneva-timestamp (unix-seconden) en x-poneva-signature, de hex-HMAC-SHA256 van “<timestamp>.<body>” met het ondertekeningsgeheim.',
    ownerOnly: 'Alleen de eigenaar van de app kan het ondertekeningsgeheim zien.',
    notEnabled: 'Deze webhook staat uit en heeft dus geen ondertekeningsgeheim.',
    failed: 'Het ondertekeningsgeheim kon niet worden getoond. Probeer het opnieuw.',
    privacy: 'Het ondertekeningsgeheim maakt geen deel uit van de chat. Het model ziet het nooit.',
  },
  en: {
    title: 'Webhook for {app}',
    urlLabel: 'Webhook URL',
    copy: 'Copy',
    copied: 'Copied',
    secretLabel: 'Signing secret',
    showSecret: 'Show signing secret',
    hideSecret: 'Hide',
    revealing: 'Showing…',
    how: 'The sender posts JSON to this URL with x-poneva-timestamp (unix seconds) and x-poneva-signature, the hex HMAC-SHA256 of “<timestamp>.<body>” under the signing secret.',
    ownerOnly: 'Only the App’s owner can see the signing secret.',
    notEnabled: 'This webhook is off, so it has no signing secret.',
    failed: 'The signing secret couldn’t be shown. Try again.',
    privacy: 'The signing secret isn’t part of the chat. The model never sees it.',
  },
  et: {
    title: 'Veebikonks rakendusele {app}',
    urlLabel: 'Veebikonksu URL',
    copy: 'Kopeeri',
    copied: 'Kopeeritud',
    secretLabel: 'Allkirjastamissaladus',
    showSecret: 'Näita allkirjastamissaladust',
    hideSecret: 'Peida',
    revealing: 'Kuvamine…',
    how: 'Saatja postitab JSON-i sellele URL-ile päistega x-poneva-timestamp (unix-sekundid) ja x-poneva-signature, mis on sõne „<timestamp>.<body>“ HMAC-SHA256 heksavormingus allkirjastamissaladusega.',
    ownerOnly: 'Allkirjastamissaladust näeb ainult rakenduse omanik.',
    notEnabled: 'See veebikonks on välja lülitatud, seega pole tal allkirjastamissaladust.',
    failed: 'Allkirjastamissaladust ei saanud kuvada. Proovi uuesti.',
    privacy: 'Allkirjastamissaladus ei kuulu vestlusesse. Mudel ei näe seda kunagi.',
  },
  fi: {
    title: 'Webhook sovellukselle {app}',
    urlLabel: 'Webhookin URL',
    copy: 'Kopioi',
    copied: 'Kopioitu',
    secretLabel: 'Allekirjoitussalaisuus',
    showSecret: 'Näytä allekirjoitussalaisuus',
    hideSecret: 'Piilota',
    revealing: 'Näytetään…',
    how: 'Lähettäjä lähettää JSON-tiedon tähän URL-osoitteeseen otsakkeilla x-poneva-timestamp (unix-sekunteja) ja x-poneva-signature, joka on merkkijonon “<timestamp>.<body>” heksa-HMAC-SHA256 allekirjoitussalaisuudella.',
    ownerOnly: 'Vain sovelluksen omistaja voi nähdä allekirjoitussalaisuuden.',
    notEnabled: 'Tämä webhook on pois päältä, joten sillä ei ole allekirjoitussalaisuutta.',
    failed: 'Allekirjoitussalaisuutta ei voitu näyttää. Yritä uudelleen.',
    privacy: 'Allekirjoitussalaisuus ei ole osa keskustelua. Malli ei näe sitä koskaan.',
  },
  fr: {
    title: 'Webhook pour {app}',
    urlLabel: 'URL du webhook',
    copy: 'Copier',
    copied: 'Copié',
    secretLabel: 'Secret de signature',
    showSecret: 'Afficher le secret de signature',
    hideSecret: 'Masquer',
    revealing: 'Affichage…',
    how: 'L’expéditeur envoie du JSON en POST à cette URL avec x-poneva-timestamp (secondes Unix) et x-poneva-signature, le HMAC-SHA256 hexadécimal de « <timestamp>.<body> » avec le secret de signature.',
    ownerOnly: 'Seul le propriétaire de l’application peut voir le secret de signature.',
    notEnabled: 'Ce webhook est désactivé, il n’a donc pas de secret de signature.',
    failed: 'Le secret de signature n’a pas pu être affiché. Réessayez.',
    privacy: 'Le secret de signature ne fait pas partie du chat. Le modèle ne le voit jamais.',
  },
  de: {
    title: 'Webhook für {app}',
    urlLabel: 'Webhook-URL',
    copy: 'Kopieren',
    copied: 'Kopiert',
    secretLabel: 'Signaturschlüssel',
    showSecret: 'Signaturschlüssel anzeigen',
    hideSecret: 'Ausblenden',
    revealing: 'Wird angezeigt…',
    how: 'Der Absender sendet JSON per POST an diese URL, mit x-poneva-timestamp (Unix-Sekunden) und x-poneva-signature, dem Hex-HMAC-SHA256 von „<timestamp>.<body>“ mit dem Signaturschlüssel.',
    ownerOnly: 'Nur die verantwortliche Person der App kann den Signaturschlüssel sehen.',
    notEnabled: 'Dieser Webhook ist aus und hat daher keinen Signaturschlüssel.',
    failed: 'Der Signaturschlüssel konnte nicht angezeigt werden. Versuchen Sie es erneut.',
    privacy: 'Der Signaturschlüssel ist nicht Teil des Chats. Das Modell sieht ihn nie.',
  },
  el: {
    title: 'Webhook για το {app}',
    urlLabel: 'URL του webhook',
    copy: 'Αντιγραφή',
    copied: 'Αντιγράφηκε',
    secretLabel: 'Μυστικό υπογραφής',
    showSecret: 'Εμφάνιση μυστικού υπογραφής',
    hideSecret: 'Απόκρυψη',
    revealing: 'Εμφάνιση…',
    how: 'Ο αποστολέας στέλνει JSON με POST σε αυτό το URL μαζί με x-poneva-timestamp (δευτερόλεπτα unix) και x-poneva-signature, το δεκαεξαδικό HMAC-SHA256 του «<timestamp>.<body>» με το μυστικό υπογραφής.',
    ownerOnly: 'Μόνο ο κάτοχος της Εφαρμογής μπορεί να δει το μυστικό υπογραφής.',
    notEnabled: 'Αυτό το webhook είναι ανενεργό, επομένως δεν έχει μυστικό υπογραφής.',
    failed: 'Δεν ήταν δυνατή η εμφάνιση του μυστικού υπογραφής. Δοκιμάστε ξανά.',
    privacy:
      'Το μυστικό υπογραφής δεν αποτελεί μέρος της συνομιλίας. Το μοντέλο δεν το βλέπει ποτέ.',
  },
  hu: {
    title: 'Webhook ehhez: {app}',
    urlLabel: 'Webhook URL-je',
    copy: 'Másolás',
    copied: 'Másolva',
    secretLabel: 'Aláírókulcs',
    showSecret: 'Aláírókulcs megjelenítése',
    hideSecret: 'Elrejtés',
    revealing: 'Megjelenítés…',
    how: 'A küldő JSON-t postáz erre az URL-re az x-poneva-timestamp (unix másodperc) és az x-poneva-signature fejléccel, ami a „<timestamp>.<body>” hexadecimális HMAC-SHA256 értéke az aláírókulccsal.',
    ownerOnly: 'Az aláírókulcsot csak az alkalmazás tulajdonosa láthatja.',
    notEnabled: 'Ez a webhook ki van kapcsolva, ezért nincs aláírókulcsa.',
    failed: 'Az aláírókulcsot nem sikerült megjeleníteni. Próbáld újra.',
    privacy: 'Az aláírókulcs nem része a csevegésnek. A modell soha nem látja.',
  },
  ga: {
    title: 'Webhook don aip {app}',
    urlLabel: 'URL an webhook',
    copy: 'Cóipeáil',
    copied: 'Cóipeáilte',
    secretLabel: 'Rún sínithe',
    showSecret: 'Taispeáin an rún sínithe',
    hideSecret: 'Folaigh',
    revealing: 'Á thaispeáint…',
    how: 'Seolann an seoltóir JSON chuig an URL seo le x-poneva-timestamp (soicindí unix) agus x-poneva-signature, an HMAC-SHA256 heicsidheachúlach de “<timestamp>.<body>” faoin rún sínithe.',
    ownerOnly: 'Ní féidir ach le húinéir na haipe an rún sínithe a fheiceáil.',
    notEnabled: 'Tá an webhook seo as, mar sin níl aon rún sínithe aige.',
    failed: 'Níorbh fhéidir an rún sínithe a thaispeáint. Bain triail eile as.',
    privacy: 'Níl an rún sínithe mar chuid den chomhrá. Ní fheiceann an tsamhail riamh é.',
  },
  it: {
    title: 'Webhook per {app}',
    urlLabel: 'URL del webhook',
    copy: 'Copia',
    copied: 'Copiato',
    secretLabel: 'Segreto di firma',
    showSecret: 'Mostra segreto di firma',
    hideSecret: 'Nascondi',
    revealing: 'Visualizzazione…',
    how: 'Il mittente invia JSON a questo URL con x-poneva-timestamp (secondi unix) e x-poneva-signature, l’HMAC-SHA256 esadecimale di “<timestamp>.<body>” con il segreto di firma.',
    ownerOnly: 'Solo il proprietario dell’app può vedere il segreto di firma.',
    notEnabled: 'Questo webhook è disattivato, quindi non ha un segreto di firma.',
    failed: 'Non è stato possibile mostrare il segreto di firma. Riprova.',
    privacy: 'Il segreto di firma non fa parte della chat. Il modello non lo vede mai.',
  },
  lv: {
    title: 'Webhook lietotnei {app}',
    urlLabel: 'Webhook URL',
    copy: 'Kopēt',
    copied: 'Nokopēts',
    secretLabel: 'Paraksta noslēpums',
    showSecret: 'Rādīt paraksta noslēpumu',
    hideSecret: 'Paslēpt',
    revealing: 'Notiek rādīšana…',
    how: 'Sūtītājs ar POST nosūta JSON uz šo URL ar x-poneva-timestamp (unix sekundes) un x-poneva-signature – “<timestamp>.<body>” HMAC-SHA256 heksadecimālā formā ar paraksta noslēpumu.',
    ownerOnly: 'Paraksta noslēpumu var redzēt tikai lietotnes īpašnieks.',
    notEnabled: 'Šis webhook ir izslēgts, tāpēc tam nav paraksta noslēpuma.',
    failed: 'Neizdevās parādīt paraksta noslēpumu. Mēģiniet vēlreiz.',
    privacy: 'Paraksta noslēpums nav tērzēšanas daļa. Modelis to nekad neredz.',
  },
  lt: {
    title: 'Programos {app} webhook',
    urlLabel: 'Webhook URL',
    copy: 'Kopijuoti',
    copied: 'Nukopijuota',
    secretLabel: 'Pasirašymo paslaptis',
    showSecret: 'Rodyti pasirašymo paslaptį',
    hideSecret: 'Slėpti',
    revealing: 'Rodoma…',
    how: 'Siuntėjas POST užklausa siunčia JSON šiuo URL su x-poneva-timestamp (unix sekundės) ir x-poneva-signature – „<timestamp>.<body>“ HMAC-SHA256 šešioliktainiu formatu su pasirašymo paslaptimi.',
    ownerOnly: 'Pasirašymo paslaptį gali matyti tik programos savininkas.',
    notEnabled: 'Šis webhook išjungtas, todėl neturi pasirašymo paslapties.',
    failed: 'Nepavyko parodyti pasirašymo paslapties. Bandykite dar kartą.',
    privacy: 'Pasirašymo paslaptis nėra pokalbio dalis. Modelis jos niekada nemato.',
  },
  mt: {
    title: 'Webhook għal {app}',
    urlLabel: 'URL tal-webhook',
    copy: 'Ikkopja',
    copied: 'Ikkupjat',
    secretLabel: 'Sigriet tal-firma',
    showSecret: 'Uri s-sigriet tal-firma',
    hideSecret: 'Aħbi',
    revealing: 'Qed jintwera…',
    how: 'Min jibgħat jibgħat JSON lil dan l-URL b’x-poneva-timestamp (sekondi unix) u x-poneva-signature, l-HMAC-SHA256 heksadeċimali ta’ “<timestamp>.<body>” bis-sigriet tal-firma.',
    ownerOnly: 'Is-sid tal-app biss jista’ jara s-sigriet tal-firma.',
    notEnabled: 'Dan il-webhook huwa mitfi, għalhekk m’għandux sigriet tal-firma.',
    failed: 'Is-sigriet tal-firma ma setax jintwera. Erġa’ pprova.',
    privacy: 'Is-sigriet tal-firma mhuwiex parti miċ-chat. Il-mudell qatt ma jarah.',
  },
  pl: {
    title: 'Webhook aplikacji {app}',
    urlLabel: 'URL webhooka',
    copy: 'Kopiuj',
    copied: 'Skopiowano',
    secretLabel: 'Klucz podpisu',
    showSecret: 'Pokaż klucz podpisu',
    hideSecret: 'Ukryj',
    revealing: 'Wyświetlanie…',
    how: 'Nadawca wysyła JSON na ten URL z nagłówkami x-poneva-timestamp (sekundy unixowe) i x-poneva-signature, czyli szesnastkowym HMAC-SHA256 z „<timestamp>.<body>” wyliczonym kluczem podpisu.',
    ownerOnly: 'Klucz podpisu widzi tylko właściciel aplikacji.',
    notEnabled: 'Ten webhook jest wyłączony, więc nie ma klucza podpisu.',
    failed: 'Nie udało się wyświetlić klucza podpisu. Spróbuj ponownie.',
    privacy: 'Klucz podpisu nie jest częścią czatu. Model nigdy go nie widzi.',
  },
  pt: {
    title: 'Webhook de {app}',
    urlLabel: 'URL do webhook',
    copy: 'Copiar',
    copied: 'Copiado',
    secretLabel: 'Segredo de assinatura',
    showSecret: 'Mostrar segredo de assinatura',
    hideSecret: 'Ocultar',
    revealing: 'Mostrando…',
    how: 'O remetente envia JSON para este URL com x-poneva-timestamp (segundos unix) e x-poneva-signature, o HMAC-SHA256 hexadecimal de “<timestamp>.<body>” com o segredo de assinatura.',
    ownerOnly: 'Somente o proprietário do aplicativo pode ver o segredo de assinatura.',
    notEnabled: 'Este webhook está desativado, então não tem segredo de assinatura.',
    failed: 'Não foi possível mostrar o segredo de assinatura. Tente novamente.',
    privacy: 'O segredo de assinatura não faz parte do chat. O modelo nunca o vê.',
  },
  ro: {
    title: 'Webhook pentru {app}',
    urlLabel: 'URL webhook',
    copy: 'Copiază',
    copied: 'Copiat',
    secretLabel: 'Secret de semnare',
    showSecret: 'Arată secretul de semnare',
    hideSecret: 'Ascunde',
    revealing: 'Se afișează…',
    how: 'Expeditorul trimite JSON la acest URL cu x-poneva-timestamp (secunde unix) și x-poneva-signature, HMAC-SHA256 hexazecimal al „<timestamp>.<body>” cu secretul de semnare.',
    ownerOnly: 'Doar proprietarul aplicației poate vedea secretul de semnare.',
    notEnabled: 'Acest webhook este dezactivat, deci nu are secret de semnare.',
    failed: 'Secretul de semnare nu a putut fi afișat. Încearcă din nou.',
    privacy: 'Secretul de semnare nu face parte din chat. Modelul nu îl vede niciodată.',
  },
  sk: {
    title: 'Webhook aplikácie {app}',
    urlLabel: 'URL webhooku',
    copy: 'Kopírovať',
    copied: 'Skopírované',
    secretLabel: 'Podpisový kľúč',
    showSecret: 'Zobraziť podpisový kľúč',
    hideSecret: 'Skryť',
    revealing: 'Zobrazuje sa…',
    how: 'Odosielateľ posiela JSON na túto URL s hlavičkami x-poneva-timestamp (unixové sekundy) a x-poneva-signature, teda hexadecimálnym HMAC-SHA256 reťazca „<timestamp>.<body>“ podpísaným podpisovým kľúčom.',
    ownerOnly: 'Podpisový kľúč vidí iba vlastník aplikácie.',
    notEnabled: 'Tento webhook je vypnutý, preto nemá podpisový kľúč.',
    failed: 'Podpisový kľúč sa nepodarilo zobraziť. Skúste to znova.',
    privacy: 'Podpisový kľúč nie je súčasťou chatu. Model ho nikdy nevidí.',
  },
  sl: {
    title: 'Webhook za aplikacijo {app}',
    urlLabel: 'URL webhooka',
    copy: 'Kopiraj',
    copied: 'Kopirano',
    secretLabel: 'Podpisni ključ',
    showSecret: 'Pokaži podpisni ključ',
    hideSecret: 'Skrij',
    revealing: 'Prikazovanje…',
    how: 'Pošiljatelj pošlje JSON na ta URL z glavama x-poneva-timestamp (unix sekunde) in x-poneva-signature, to je šestnajstiški HMAC-SHA256 niza „<timestamp>.<body>“ s podpisnim ključem.',
    ownerOnly: 'Podpisni ključ lahko vidi samo lastnik aplikacije.',
    notEnabled: 'Ta webhook je izklopljen, zato nima podpisnega ključa.',
    failed: 'Podpisnega ključa ni bilo mogoče prikazati. Poskusite znova.',
    privacy: 'Podpisni ključ ni del klepeta. Model ga nikoli ne vidi.',
  },
  es: {
    title: 'Webhook de {app}',
    urlLabel: 'URL del webhook',
    copy: 'Copiar',
    copied: 'Copiado',
    secretLabel: 'Secreto de firma',
    showSecret: 'Mostrar secreto de firma',
    hideSecret: 'Ocultar',
    revealing: 'Mostrando…',
    how: 'El remitente envía JSON a esta URL con x-poneva-timestamp (segundos unix) y x-poneva-signature, el HMAC-SHA256 en hexadecimal de “<timestamp>.<body>” con el secreto de firma.',
    ownerOnly: 'Solo el propietario de la app puede ver el secreto de firma.',
    notEnabled: 'Este webhook está desactivado, así que no tiene secreto de firma.',
    failed: 'No se pudo mostrar el secreto de firma. Inténtalo de nuevo.',
    privacy: 'El secreto de firma no forma parte del chat. El modelo nunca lo ve.',
  },
  sv: {
    title: 'Webhook för {app}',
    urlLabel: 'Webhookens URL',
    copy: 'Kopiera',
    copied: 'Kopierad',
    secretLabel: 'Signeringsnyckel',
    showSecret: 'Visa signeringsnyckeln',
    hideSecret: 'Dölj',
    revealing: 'Visar…',
    how: 'Avsändaren skickar JSON till den här URL:en med x-poneva-timestamp (unixsekunder) och x-poneva-signature, hex-HMAC-SHA256 av ”<timestamp>.<body>” med signeringsnyckeln.',
    ownerOnly: 'Bara appens ägare kan se signeringsnyckeln.',
    notEnabled: 'Webhooken är avstängd, så den har ingen signeringsnyckel.',
    failed: 'Signeringsnyckeln kunde inte visas. Försök igen.',
    privacy: 'Signeringsnyckeln är inte en del av chatten. Modellen ser den aldrig.',
  },
};
export default copy;
